import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Plus, Trash2, Copy, GripVertical } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useToast } from '@/components/ToastContainer'
import { apiFetch } from '@/lib/api'
import { Button, Input, Select, Spinner, Alert, Toggle, Drawer } from '@/ui'
import styles from './ProductCatalog.module.css'

type PaymentType =
  | 'PREMIUM_UPGRADE'
  | 'AI_CREDITS'
  | 'AI_CREDITS_BUNDLE'
  | 'INVITATION_PACKAGE'
  | 'CREDITS_10'
  | 'CREDITS_20'
  | 'CREDITS_30'
  | 'CREDITS_50'
  | 'CREDITS_100'
  | 'INVITATION_MAIN'
  | 'STUDIO_PHOTOS_10'
  | 'STUDIO_PHOTOS_20'
  | 'STUDIO_PHOTOS_30'
  | 'SNAP_MAIN'
  | 'SNAP_PHOTOS_10'
  | 'SNAP_PHOTOS_20'
  | 'THANKYOU_ADDON'
  | 'ALL_PACKAGE'
  | 'THANKS_PACKAGE'

// 사이트 + 스마트스토어 표준 상품을 상단에 노출, 레거시는 하단에.
const PAYMENT_TYPES: PaymentType[] = [
  'INVITATION_PACKAGE',
  'STUDIO_PHOTOS_10',
  'STUDIO_PHOTOS_20',
  'STUDIO_PHOTOS_30',
  'SNAP_MAIN',
  'SNAP_PHOTOS_10',
  'SNAP_PHOTOS_20',
  'THANKYOU_ADDON',
  'ALL_PACKAGE',
  'THANKS_PACKAGE',
  'CREDITS_10',
  'CREDITS_20',
  'CREDITS_30',
  'CREDITS_50',
  'CREDITS_100',
  'PREMIUM_UPGRADE',
  'AI_CREDITS',
  'AI_CREDITS_BUNDLE',
]

type Channel = 'SITE' | 'SMARTSTORE'
const CHANNELS: Channel[] = ['SITE', 'SMARTSTORE']

type ProductCategory = 'wedding' | 'studio'
const CATEGORIES: ProductCategory[] = ['wedding', 'studio']

// 분류 파생 — 구매 페이지(getProductCategory, packages/shared)와 동일 규칙.
// category 필드 우선, 없으면 type/activatesAccount에서 파생.
function deriveCategory(p: ProductDefinition): ProductCategory {
  if (p.category) return p.category
  if (p.activatesAccount) return 'wedding'
  if (p.type === 'THANKS_PACKAGE' || p.type === 'THANKYOU_ADDON') return 'wedding'
  return 'studio'
}

const CATEGORY_LABELS: Record<ProductCategory, string> = {
  wedding: '청첩장 · 감사장',
  studio: 'AI 스튜디오',
}

interface ProductDefinition {
  type: PaymentType
  nameKo: string
  credits: number
  price: number
  basePrice: number
  discountPercent: number
  channels: Channel[]
  activatesAccount: boolean
  requiresActivation: boolean
  /** 셀프 환불 허용 여부. false=차단(본품) */
  selfRefundable?: boolean
  /** 구매 페이지 분류 탭 */
  category?: ProductCategory
  /** 부여 범용 활성화 슬롯(청첩장·감사장 공용). 미지정=0 */
  universalSlots?: number
  /** 부여 청첩장 전용 활성화 슬롯. 미지정=0 */
  invitationSlots?: number
  /** 부여 감사장 전용 활성화 슬롯. 미지정=0 */
  thankyouSlots?: number
  /** 구매 페이지 '인기' 배지 수동 지정 */
  popular?: boolean
  /** 구매 페이지 카드 우상단 프로모 문구 */
  promoBadge?: string
  /** 구매 페이지 카드 체크 항목(추가정보) */
  features?: string[]
  /** 구매 페이지 카드 상품명 아래 짧은 설명 문구 */
  description?: string
}

interface SettingRow {
  key: string
  value: unknown
  category: string
  label: string
  description: string | null
}

const EMPTY_PRODUCT: ProductDefinition = {
  type: 'STUDIO_PHOTOS_10',
  nameKo: '',
  credits: 0,
  price: 0,
  basePrice: 0,
  discountPercent: 0,
  channels: ['SITE', 'SMARTSTORE'],
  activatesAccount: false,
  requiresActivation: true,
  selfRefundable: true,
  category: 'studio',
}

interface OptionEntry {
  optionCode: string
  label: string
  credits: number
  activates: boolean
  paymentType: PaymentType
}

export function ProductCatalog() {
  const { data, isLoading, refetch } = useApiFetch<SettingRow[]>(
    '/api/admin/settings?category=billing',
  )
  const [catalogDraft, setCatalogDraft] = useState<ProductDefinition[]>([])
  const [optionMapDraft, setOptionMapDraft] = useState<OptionEntry[]>([])
  const [savingCatalog, setSavingCatalog] = useState(false)
  const [savingOptionMap, setSavingOptionMap] = useState(false)
  // 상품 상세/편집 Drawer — idx는 catalogDraft 인덱스, mode로 보기/수정 분기
  const [drawer, setDrawer] = useState<{ idx: number; mode: 'view' | 'edit' } | null>(null)
  const { addToast } = useToast()

  const catalogRow = useMemo(
    () => (data ?? []).find((r) => r.key === 'product_catalog') ?? null,
    [data],
  )
  const optionMapRow = useMemo(
    () => (data ?? []).find((r) => r.key === 'option_credit_map') ?? null,
    [data],
  )

  useEffect(() => {
    if (catalogRow && Array.isArray(catalogRow.value)) {
      setCatalogDraft(catalogRow.value as ProductDefinition[])
    }
  }, [catalogRow])

  const seedOptionMapToDb = async (mode: 'merge' | 'overwrite') => {
    if (
      mode === 'overwrite' &&
      !confirm('DB의 옵션 매핑을 코드 기본값으로 완전히 덮어씁니다. 진행할까요?')
    )
      return
    try {
      const result = await apiFetch<{ mode: string; added: number; total: number }>(
        '/api/admin/option-credit-map/seed',
        { method: 'POST', body: JSON.stringify({ mode }) },
      )
      addToast({
        type: 'success',
        message: `DB 시드 완료: ${mode === 'overwrite' ? '전체 교체' : `${result.added}건 추가`} (총 ${result.total}건)`,
      })
      refetch()
    } catch (e: any) {
      addToast({ type: 'error', message: `시드 실패: ${e.message}` })
    }
  }

  const loadOptionDefaults = async (replaceExisting: boolean) => {
    try {
      const defaults = await apiFetch<Record<string, Omit<OptionEntry, 'optionCode'>>>(
        '/api/admin/option-credit-map/defaults',
      )
      const entries = Object.entries(defaults).map(([optionCode, v]) => ({ optionCode, ...v }))
      if (replaceExisting) {
        setOptionMapDraft(entries)
      } else {
        // 기존 draft에 빠진 optionCode만 추가 (병합)
        setOptionMapDraft((cur) => {
          const have = new Set(cur.map((r) => r.optionCode))
          const additions = entries.filter((e) => !have.has(e.optionCode))
          return additions.length > 0 ? [...cur, ...additions] : cur
        })
      }
      addToast({
        type: 'success',
        message: replaceExisting ? '기본값으로 초기화됨' : `${entries.length}개 기본값 불러옴`,
      })
    } catch (e: any) {
      addToast({ type: 'error', message: `기본값 로드 실패: ${e.message}` })
    }
  }

  useEffect(() => {
    if (optionMapRow && optionMapRow.value && typeof optionMapRow.value === 'object') {
      const entries = Object.entries(
        optionMapRow.value as Record<string, Omit<OptionEntry, 'optionCode'>>,
      ).map(([optionCode, v]) => ({ optionCode, ...v }))
      setOptionMapDraft(entries)
    } else if (data && !optionMapRow) {
      // DB row 미존재 시 코드 default를 fetch해서 draft 시드
      void loadOptionDefaults(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionMapRow, data])

  // 가격 = 정가 × (1 - 할인%/100). 정가·할인%가 바뀌면 가격을 자동 재계산한다(가격은 읽기전용).
  const computePrice = (basePrice: number, discountPercent: number): number => {
    const pct = Math.min(99, Math.max(0, discountPercent))
    return Math.round(basePrice * (1 - pct / 100))
  }

  const updateCatalog = (idx: number, patch: Partial<ProductDefinition>) => {
    setCatalogDraft((rows) =>
      rows.map((r, i) => {
        if (i !== idx) return r
        const next = { ...r, ...patch }
        // 정가·할인% 변경 시 가격 자동 계산
        if ('basePrice' in patch || 'discountPercent' in patch) {
          next.price = computePrice(next.basePrice, next.discountPercent)
        }
        return next
      }),
    )
  }

  const toggleChannel = (idx: number, ch: Channel) => {
    setCatalogDraft((rows) =>
      rows.map((r, i) => {
        if (i !== idx) return r
        const has = r.channels.includes(ch)
        const channels = has ? r.channels.filter((c) => c !== ch) : [...r.channels, ch]
        return { ...r, channels }
      }),
    )
  }

  // Drawer 닫기. 저장 전 빈 신규 상품(이름 없는 마지막 행)은 취소 시 draft에서 제거한다.
  const closeDrawer = () => {
    if (drawer?.mode === 'edit') {
      const p = catalogDraft[drawer.idx]
      if (p && !p.nameKo?.trim() && drawer.idx === catalogDraft.length - 1) {
        setCatalogDraft((rows) => rows.slice(0, -1))
      }
    }
    setDrawer(null)
  }

  const duplicateProduct = (idx: number) => {
    setCatalogDraft((rows) => {
      const copy = { ...rows[idx] }
      return [...rows.slice(0, idx + 1), copy, ...rows.slice(idx + 1)]
    })
  }

  const removeProduct = (idx: number) => {
    setCatalogDraft((rows) => rows.filter((_, i) => i !== idx))
  }

  // 같은 분류 안에서만 drag&drop 재정렬. 전역 catalogDraft 배열에서 두 type의
  // 위치를 직접 arrayMove → 배열 순서가 곧 노출 순서이므로 그대로 저장된다.
  const handleReorder = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setCatalogDraft((rows) => {
      const from = rows.findIndex((p) => p.type === active.id)
      const to = rows.findIndex((p) => p.type === over.id)
      if (from === -1 || to === -1) return rows
      return arrayMove(rows, from, to)
    })
  }

  const addOptionRow = () => {
    setOptionMapDraft((rows) => [
      ...rows,
      { optionCode: '', label: '', credits: 0, activates: false, paymentType: 'AI_CREDITS' },
    ])
  }

  const updateOptionRow = (idx: number, patch: Partial<OptionEntry>) => {
    setOptionMapDraft((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }

  const removeOptionRow = (idx: number) => {
    setOptionMapDraft((rows) => rows.filter((_, i) => i !== idx))
  }

  const validateCatalog = (): string | null => {
    const seen = new Set<string>()
    for (const p of catalogDraft) {
      if (!p.nameKo.trim()) return `${p.type}: 상품명이 비어 있습니다`
      if (p.price < 0) return `${p.type}: 가격은 0 이상이어야 합니다`
      if (p.basePrice < 0) return `${p.type}: 정가는 0 이상이어야 합니다`
      if (p.discountPercent < 0 || p.discountPercent > 99)
        return `${p.type}: 할인율은 0-99 사이여야 합니다`
      if (p.credits < 0) return `${p.type}: 크레딧은 0 이상이어야 합니다`
      if (p.channels.length === 0) return `${p.type}: 채널을 최소 1개 선택하세요`
      if (seen.has(p.type)) return `${p.type}: 중복된 타입이 있습니다`
      seen.add(p.type)
    }
    return null
  }

  const validateOptionMap = (): string | null => {
    const seen = new Set<string>()
    for (const r of optionMapDraft) {
      if (!r.optionCode.trim()) return 'optionCode가 비어 있는 행이 있습니다'
      if (seen.has(r.optionCode)) return `중복된 optionCode: ${r.optionCode}`
      if (r.credits < 0) return `${r.optionCode}: 크레딧은 0 이상이어야 합니다`
      if (!r.label.trim()) return `${r.optionCode}: 라벨이 비어 있습니다`
      seen.add(r.optionCode)
    }
    return null
  }

  const handleSaveCatalog = async (): Promise<boolean> => {
    const err = validateCatalog()
    if (err) {
      addToast({ type: 'error', message: err })
      return false
    }
    setSavingCatalog(true)
    try {
      // features의 빈 항목은 저장 시 제거 (빈 체크 항목 노출 방지)
      const value = catalogDraft.map((p) => {
        if (!p.features) return p
        const cleaned = p.features.map((f) => f.trim()).filter(Boolean)
        return { ...p, features: cleaned }
      })
      await apiFetch('/api/admin/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          key: 'product_catalog',
          value,
          category: 'billing',
          label: '상품 카탈로그',
          description: '판매 상품·가격·크레딧 정의',
        }),
      })
      addToast({ type: 'success', message: '카탈로그 저장 완료' })
      refetch()
      return true
    } catch (e: any) {
      addToast({ type: 'error', message: e.message || '저장 실패' })
      return false
    } finally {
      setSavingCatalog(false)
    }
  }

  const handleSaveOptionMap = async () => {
    const err = validateOptionMap()
    if (err) {
      addToast({ type: 'error', message: err })
      return
    }
    setSavingOptionMap(true)
    try {
      const value: Record<string, Omit<OptionEntry, 'optionCode'>> = {}
      for (const r of optionMapDraft) {
        value[r.optionCode.trim()] = {
          label: r.label,
          credits: r.credits,
          activates: r.activates,
          paymentType: r.paymentType,
        }
      }
      await apiFetch('/api/admin/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          key: 'option_credit_map',
          value,
          category: 'billing',
          label: '스마트스토어 옵션 → 크레딧/활성화 매핑',
          description: 'Naver optionCode → 라벨/크레딧/활성화/PaymentType',
        }),
      })
      addToast({ type: 'success', message: '옵션 매핑 저장 완료' })
      refetch()
    } catch (e: any) {
      addToast({ type: 'error', message: e.message || '저장 실패' })
    } finally {
      setSavingOptionMap(false)
    }
  }

  // 분류별로 묶되 전역 배열 순서를 보존 (각 그룹 내 순서 = 노출 순서).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )
  const productsByCategory = useMemo(() => {
    const groups: Record<ProductCategory, ProductDefinition[]> = { wedding: [], studio: [] }
    for (const p of catalogDraft) groups[deriveCategory(p)].push(p)
    return groups
  }, [catalogDraft])

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>상품/가격 관리</h1>
          <p className={styles.subtitle}>
            판매 상품 카탈로그와 스마트스토어 상품코드 → PaymentType 매핑을 DB 에서 관리합니다.
            사이트와 스마트스토어 상품 구성은 1:1 매칭됩니다 (옵션 ID는 하단 ‘스마트스토어 옵션
            매핑’에서 관리).
          </p>
        </div>
      </div>

      <Alert variant="info" title="적용 시점">
        저장 즉시 서버 인스턴스의 캐시가 초기화됩니다. 다중 인스턴스 환경에서는 최대 5분까지 분산
        반영 지연이 있을 수 있습니다.
      </Alert>

      {/* ============ Section 1: 상품 카탈로그 ============ */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>상품 카탈로그</h2>
            <p className={styles.sectionDesc}>
              사이트 결제 · 스마트스토어 금액 검증 · 크레딧 지급에 사용됩니다. 분류별로 핸들(⋮⋮)을
              끌어 구매 페이지 노출 순서를 바꾼 뒤 “카탈로그 저장”을 누르세요.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="secondary"
              onClick={() => {
                setCatalogDraft((rows) => [...rows, { ...EMPTY_PRODUCT }])
                // 추가 전 길이가 곧 새 항목 인덱스 — edit Drawer로 바로 연다
                setDrawer({ idx: catalogDraft.length, mode: 'edit' })
              }}
            >
              <Plus size={14} /> 상품 추가
            </Button>
            <Button variant="primary" onClick={handleSaveCatalog} disabled={savingCatalog}>
              {savingCatalog ? '저장 중...' : '카탈로그 저장'}
            </Button>
          </div>
        </div>

        {!catalogRow && (
          <Alert variant="warning" title="DB row 미존재">
            app_settings 에 'product_catalog' row 가 없어 기본값이 사용 중입니다. 저장하면 DB 에
            생성됩니다.
          </Alert>
        )}

        {catalogDraft.length === 0 ? (
          <div className={styles.emptyRow}>상품이 없습니다. 상단 버튼으로 추가하세요.</div>
        ) : (
          CATEGORIES.map((cat) => {
            const items = productsByCategory[cat]
            return (
              <div key={cat} className={styles.catalogGroup}>
                <h3 className={styles.catalogGroupTitle}>
                  {CATEGORY_LABELS[cat]}
                  <span className={styles.catalogGroupCount}>{items.length}</span>
                </h3>
                {items.length === 0 ? (
                  <div className={styles.emptyRow}>이 분류의 상품이 없습니다.</div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleReorder}
                  >
                    <SortableContext
                      items={items.map((p) => p.type)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className={styles.sortList}>
                        {items.map((p) => (
                          <SortableProductRow
                            key={p.type}
                            product={p}
                            onView={() =>
                              setDrawer({ idx: catalogDraft.indexOf(p), mode: 'view' })
                            }
                            onEdit={() =>
                              setDrawer({ idx: catalogDraft.indexOf(p), mode: 'edit' })
                            }
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            )
          })
        )}

        {drawer && catalogDraft[drawer.idx] && (
          <Drawer
            open
            onClose={() => closeDrawer()}
            title={drawer.mode === 'view' ? '상품 상세' : '상품 수정'}
            width={560}
          >
            {drawer.mode === 'view' ? (
              <ProductView
                product={catalogDraft[drawer.idx]}
                onEdit={() => setDrawer({ idx: drawer.idx, mode: 'edit' })}
                onDuplicate={() => {
                  duplicateProduct(drawer.idx)
                  setDrawer(null)
                }}
                onDelete={() => {
                  removeProduct(drawer.idx)
                  setDrawer(null)
                }}
              />
            ) : (
              <ProductEditForm
                product={catalogDraft[drawer.idx]}
                onChange={(patch) => updateCatalog(drawer.idx, patch)}
                onToggleChannel={(ch) => toggleChannel(drawer.idx, ch)}
                onSave={async () => {
                  const ok = await handleSaveCatalog()
                  if (ok) setDrawer(null)
                }}
                onCancel={() => closeDrawer()}
                saving={savingCatalog}
              />
            )}
          </Drawer>
        )}
      </section>

      {/* ============ Section 2: 스마트스토어 옵션 매핑 ============ */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>스마트스토어 옵션 매핑</h2>
            <p className={styles.sectionDesc}>
              Naver optionCode(메인 상품 = productId, 추가구성 = itemNo) → 크레딧/활성화/PaymentType
              매핑. 주문번호 활성화 시 모든 productOrder의 optionCode를 lookup해서 (credits ×
              quantity)를 합산 지급한다.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              variant="ghost"
              onClick={() => void seedOptionMapToDb('merge')}
              title="코드 기본값 중 DB에 없는 항목만 즉시 DB에 추가 (편집 중인 표 무시)"
            >
              ⚡ DB 시드 (병합)
            </Button>
            <Button
              variant="ghost"
              onClick={() => void seedOptionMapToDb('overwrite')}
              title="DB 옵션 매핑을 코드 기본값으로 완전 교체 (편집 중인 표 무시)"
            >
              ⚡ DB 시드 (전체 교체)
            </Button>
            <Button
              variant="ghost"
              onClick={() => loadOptionDefaults(false)}
              title="현재 편집 중인 표에 코드 기본값 중 빠진 항목만 보충 (저장 전)"
            >
              기본값 표에 병합
            </Button>
            <Button variant="secondary" onClick={addOptionRow}>
              <Plus size={14} /> 행 추가
            </Button>
            <Button variant="primary" onClick={handleSaveOptionMap} disabled={savingOptionMap}>
              {savingOptionMap ? '저장 중...' : '매핑 저장'}
            </Button>
          </div>
        </div>

        {!optionMapRow && (
          <Alert variant="info" title="기본값 로드됨 — 저장하면 DB에 시드됩니다">
            app_settings 에 'option_credit_map' row 가 없어 코드의 기본 매핑을 불러왔습니다. 필요한
            항목(예: 30장 옵션) 추가 후 "매핑 저장"을 누르면 DB에 생성됩니다.
          </Alert>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>optionCode</th>
                <th>라벨</th>
                <th>크레딧 (단위 수량)</th>
                <th>활성화</th>
                <th>PaymentType</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {optionMapDraft.length === 0 && (
                <tr>
                  <td colSpan={6} className={styles.emptyRow}>
                    매핑이 없습니다. 상단 버튼으로 추가하세요.
                  </td>
                </tr>
              )}
              {optionMapDraft.map((r, idx) => (
                <tr key={idx}>
                  <td>
                    <Input
                      className={styles.codeInput}
                      value={r.optionCode}
                      onChange={(e) => updateOptionRow(idx, { optionCode: e.target.value })}
                      placeholder="13257373714"
                    />
                  </td>
                  <td>
                    <Input
                      className={styles.textInput}
                      value={r.label}
                      onChange={(e) => updateOptionRow(idx, { label: e.target.value })}
                      placeholder="청첩장"
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      className={styles.numInput}
                      value={r.credits}
                      onChange={(e) => updateOptionRow(idx, { credits: Number(e.target.value) })}
                    />
                  </td>
                  <td>
                    <Toggle
                      checked={r.activates}
                      onChange={(checked) => updateOptionRow(idx, { activates: checked })}
                    />
                  </td>
                  <td>
                    <Select
                      value={r.paymentType}
                      onChange={(e) =>
                        updateOptionRow(idx, { paymentType: e.target.value as PaymentType })
                      }
                      options={PAYMENT_TYPES.map((t) => ({ label: t, value: t }))}
                    />
                  </td>
                  <td>
                    <div className={styles.tableActions}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeOptionRow(idx)}
                        title="삭제"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

// ============ 정렬 가능한 상품 행 ============
// 드래그 핸들(GripVertical)로만 순서 변경, 행 클릭은 view·수정 버튼은 edit으로 보존.
function SortableProductRow({
  product,
  onView,
  onEdit,
}: {
  product: ProductDefinition
  onView: () => void
  onEdit: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: product.type,
  })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  }
  return (
    <div ref={setNodeRef} style={style} className={styles.sortRow} onClick={onView}>
      <button
        type="button"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        aria-label="드래그하여 순서 변경"
        className={styles.dragHandle}
      >
        <GripVertical size={16} />
      </button>
      <span className={styles.sortType}>{product.type}</span>
      <span className={styles.sortName}>
        {product.nameKo || <span className={styles.muted}>(이름 없음)</span>}
      </span>
      <span className={styles.sortPrice}>
        {product.discountPercent > 0 && (
          <s className={styles.muted}>{product.basePrice.toLocaleString()}</s>
        )}
        {product.price.toLocaleString()}원
      </span>
      <span className={styles.channelsCell}>
        {product.channels.map((ch) => (
          <span key={ch} className={`${styles.channelChip} ${styles.channelChipActive}`}>
            {ch}
          </span>
        ))}
      </span>
      <Button
        variant="secondary"
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          onEdit()
        }}
      >
        수정
      </Button>
    </div>
  )
}

// ============ 상품 상세 (읽기 전용) ============
function ProductView({
  product,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  product: ProductDefinition
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
}) {
  const selfRefundable = product.selfRefundable ?? !product.activatesAccount
  const rows: [string, ReactNode][] = [
    ['타입', product.type],
    ['상품명', product.nameKo],
    ['상품 설명', product.description || '-'],
    ['정가', `${product.basePrice.toLocaleString()}원`],
    ['할인%', `${product.discountPercent}%`],
    ['가격', `${product.price.toLocaleString()}원`],
    ['크레딧', product.credits],
    ['범용슬롯', product.universalSlots ?? 0],
    ['청첩장슬롯', product.invitationSlots ?? 0],
    ['감사장슬롯', product.thankyouSlots ?? 0],
    ['채널', product.channels.join(', ')],
    ['셀프환불', selfRefundable ? '허용' : '차단(본품)'],
    ['분류', product.category ?? '-'],
    ['인기', product.popular ? 'O' : '-'],
    ['프로모 문구', product.promoBadge || '-'],
    [
      '추가정보',
      product.features && product.features.length > 0 ? (
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          {product.features.map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ul>
      ) : (
        '- (기본 문구)'
      ),
    ],
  ]
  return (
    <div className={styles.viewWrap}>
      <dl className={styles.viewList}>
        {rows.map(([k, v]) => (
          <div key={k} className={styles.viewRow}>
            <dt className={styles.viewKey}>{k}</dt>
            <dd className={styles.viewVal}>{v}</dd>
          </div>
        ))}
      </dl>
      <div className={styles.editActions}>
        <Button variant="ghost" onClick={onDuplicate}>
          <Copy size={14} /> 복제
        </Button>
        <Button variant="ghost" onClick={onDelete}>
          <Trash2 size={14} /> 삭제
        </Button>
        <Button variant="primary" onClick={onEdit}>
          수정
        </Button>
      </div>
    </div>
  )
}

// ============ 상품 편집 폼 ============
function ProductEditForm({
  product,
  onChange,
  onToggleChannel,
  onSave,
  onCancel,
  saving,
}: {
  product: ProductDefinition
  onChange: (patch: Partial<ProductDefinition>) => void
  onToggleChannel: (ch: Channel) => void
  onSave: () => void
  onCancel: () => void
  saving: boolean
}) {
  return (
    <div className={styles.editForm}>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>타입</span>
        <Select
          value={product.type}
          onChange={(e) => onChange({ type: e.target.value as PaymentType })}
          options={PAYMENT_TYPES.map((t) => ({ label: t, value: t }))}
        />
      </label>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>상품명(KO)</span>
        <Input value={product.nameKo} onChange={(e) => onChange({ nameKo: e.target.value })} placeholder="AI 사진 10장" />
      </label>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>상품 설명</span>
        <Input
          value={product.description ?? ''}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="구매 페이지 상품명 아래 표시되는 짧은 설명 (예: 나만의 모바일 청첩장 제작)"
        />
      </label>
      <div className={styles.editGrid}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>정가</span>
          <Input type="number" value={product.basePrice} onChange={(e) => onChange({ basePrice: Number(e.target.value) })} />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>할인%</span>
          <Input type="number" min={0} max={99} value={product.discountPercent} onChange={(e) => onChange({ discountPercent: Number(e.target.value) })} />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>가격(자동)</span>
          <Input type="number" value={product.price} readOnly disabled title="정가 × (1 − 할인%) 자동 계산" />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>크레딧</span>
          <Input type="number" value={product.credits} onChange={(e) => onChange({ credits: Number(e.target.value) })} />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>범용슬롯</span>
          <Input type="number" value={product.universalSlots ?? 0} onChange={(e) => onChange({ universalSlots: Number(e.target.value) })} />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>청첩장슬롯</span>
          <Input type="number" value={product.invitationSlots ?? 0} onChange={(e) => onChange({ invitationSlots: Number(e.target.value) })} />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>감사장슬롯</span>
          <Input type="number" value={product.thankyouSlots ?? 0} onChange={(e) => onChange({ thankyouSlots: Number(e.target.value) })} />
        </label>
      </div>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>채널</span>
        <div className={styles.channelsCell}>
          {CHANNELS.map((ch) => (
            <button
              type="button"
              key={ch}
              className={`${styles.channelChip} ${product.channels.includes(ch) ? styles.channelChipActive : ''}`}
              onClick={() => onToggleChannel(ch)}
            >
              {ch}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.editGrid}>
        <div className={styles.field}>
          <span className={styles.fieldLabel} title="끄면 셀프 환불 차단(본품)">셀프환불</span>
          <Toggle checked={product.selfRefundable ?? !product.activatesAccount} onChange={(c) => onChange({ selfRefundable: c })} />
        </div>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>분류</span>
          <Select
            value={product.category ?? ''}
            onChange={(e) => onChange({ category: e.target.value as ProductCategory })}
            options={CATEGORIES.map((c) => ({ label: c, value: c }))}
          />
        </label>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>인기</span>
          <Toggle checked={product.popular ?? false} onChange={(c) => onChange({ popular: c })} />
        </div>
      </div>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>프로모 문구</span>
        <Input value={product.promoBadge ?? ''} onChange={(e) => onChange({ promoBadge: e.target.value })} placeholder="예: 기간한정 AI 사용권 10장 포함" />
      </label>
      <div className={styles.field}>
        <span className={styles.fieldLabel} title="구매 페이지 카드 체크 항목. 비우면 type별 기본 문구가 표시됩니다">추가정보(체크 항목)</span>
        {(product.features ?? []).map((f, i) => (
          <div key={i} className={styles.featureRow}>
            <Input
              value={f}
              onChange={(e) => {
                const next = [...(product.features ?? [])]
                next[i] = e.target.value
                onChange({ features: next })
              }}
              placeholder="예: AI 사진 10장 생성"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange({ features: (product.features ?? []).filter((_, j) => j !== i) })}
              title="삭제"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onChange({ features: [...(product.features ?? []), ''] })}
        >
          <Plus size={14} /> 항목 추가
        </Button>
      </div>
      <div className={styles.editActions}>
        <Button variant="secondary" onClick={onCancel}>
          취소
        </Button>
        <Button variant="primary" onClick={onSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </Button>
      </div>
    </div>
  )
}
